import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ProductConditionCode } from "../../common/models/CommonModels";
import {
  ChangeStockItemModel,
  CustomerInventoryState,
  StockBatchUpload,
  StockItemsQuery,
  StockItemsQueryParameter
} from "./models/models";
import customerStockItemsApi from "../shared/api/customerStockItemsApi";
import customerManufacturersApi from "../shared/api/customerManufacturersApi";
import customerCategoriesApi from "../shared/api/customerCategoriesApi";
import customerStockBatchesApi from "../shared/api/customerStockBatchesApi";
import customerStockItemGroupsApi from "../shared/api/customerStockItemGroupsApi";

export const getStockItems = createAsyncThunk(
  "customerInventory/getStockItems",
  async (query: StockItemsQuery) => {
    return customerStockItemsApi.getStockItems(query);
  }
);

export const getStockItem = createAsyncThunk(
  "customerInventory/getStockItem",
  async (id: string) => {
    return customerStockItemsApi.getStockItem(id);
  }
);

export const createStockItem = createAsyncThunk(
  "customerInventory/createStockItem",
  async (model: ChangeStockItemModel) => {
    return customerStockItemsApi.createStockItem(model);
  }
);

export const changeStockItem = createAsyncThunk(
  "customerInventory/changeStockItem",
  async ({ id, model }: any) => {
    return customerStockItemsApi.changeStockItem(id, model);
  }
);

export const moveStockItemsToGroup = createAsyncThunk(
  "customerInventory/moveStockItemsToGroup",
  async ({ groupId, ids }: any) => {
    return customerStockItemsApi.moveStockItemsToGroup(groupId, ids);
  }
);

export const deleteStockItem = createAsyncThunk(
  "customerInventory/deleteStockItem",
  async (id: string) => {
    return customerStockItemsApi.deleteStockItem(id);
  }
);

export const deleteStockItems = createAsyncThunk(
  "customerInventory/deleteStockItems",
  async (ids: string[]) => {
    return customerStockItemsApi.deleteStockItems(ids);
  }
);

export const uploadStockItems = createAsyncThunk(
  "customerInventory/uploadStockItems",
  async (model: StockBatchUpload) => {
    return customerStockBatchesApi.uploadFile(model);
  }
);

export const getManufacturers = createAsyncThunk(
  "customerInventory/getManufacturers",
  async () => {
    return customerManufacturersApi.getManufacturers();
  }
);

export const getCategories = createAsyncThunk(
  "customerInventory/getCategories",
  async () => {
    return customerCategoriesApi.getCategories();
  }
);

export const getStockItemGroups = createAsyncThunk(
  "customerInventory/getStockItemGroups",
  async () => {
    return customerStockItemGroupsApi.getStockItemGroups();
  }
);

const initialState: CustomerInventoryState = {
  query: {
    search: undefined,
    groupId: undefined,
    conditionCodes: [
      ProductConditionCode.new,
      ProductConditionCode.used,
      ProductConditionCode.refit,
      ProductConditionCode.notApplicable
    ],
    sortBy: StockItemsQueryParameter.number,
    sortDesc: false,
    pageNo: 1,
    pageSize: 20
  },
  stockItems: undefined,
  stockItem: undefined,
  stockItemsUploadResult: undefined,
  manufacturers: [],
  categories: [],
  groups: [],
  loading: false,
  success: undefined,
  error: undefined
};

const customerInventorySlice = createSlice({
  name: "customerInventory",
  initialState: initialState,
  reducers: {
    changeQuery: (state, action) => {
      state.query = action.payload;
    },
    selectStockItem: (state, { payload }) => {
      state.stockItem = payload;
    },
    setStockItemsUploadResult: (state, { payload }) => {
      state.stockItemsUploadResult = payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getStockItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(getStockItems.fulfilled, (state, { payload }) => {
        state.stockItems = payload;
        state.loading = false;
      })
      .addCase(getStockItems.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getStockItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(getStockItem.fulfilled, (state, { payload }) => {
        state.stockItem = payload;
        state.loading = false;
      })
      .addCase(getStockItem.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(createStockItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(createStockItem.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(createStockItem.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(changeStockItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeStockItem.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(changeStockItem.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(uploadStockItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadStockItems.fulfilled, (state, { payload }) => {
        state.stockItemsUploadResult = payload;
        state.loading = false;
      })
      .addCase(uploadStockItems.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getManufacturers.fulfilled, (state, { payload }) => {
        state.manufacturers = payload;
      });

    builder
      .addCase(getCategories.fulfilled, (state, { payload }) => {
        state.categories = payload;
      });

    builder
      .addCase((getStockItemGroups.fulfilled), (state, { payload }) => {
        state.groups = payload
      })
  }
});

export const {
  changeQuery,
  selectStockItem,
  setStockItemsUploadResult,
  setSuccess,
  setError
} = customerInventorySlice.actions;

export default customerInventorySlice.reducer;
